import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import LayoutHOC from "./LayoutHOC";
import { DASHBOARD_STATS } from "../utils/constants";

const Dashboard = () => {
  const user = useSelector((state) => state.user);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.token) return;
      
      try {
        const response = await fetch(DASHBOARD_STATS, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  const StatCard = ({ title, value, subtitle, icon, color, link }) => (
    <Link to={link} className="block">
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{title}</p>
            <p className={`text-3xl font-bold mt-2 ${color}`}>{loading ? "..." : value || 0}</p>
            {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
            <i className={`bi ${icon} text-2xl ${color}`}></i>
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="p-6 mt-16 min-h-screen bg-gray-50">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back, {user?.name || "Admin"}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Total Jobs"
          value={stats?.all_jobs}
          subtitle={`${stats?.active_job || 0} active`}
          icon="bi-briefcase"
          color="text-blue-600"
          link="/jobs_list"
        />
        <StatCard
          title="Employers"
          value={stats?.employer}
          subtitle={`${stats?.active_employer || 0} active`}
          icon="bi-building"
          color="text-green-600"
          link="/employer"
        />
        <StatCard
          title="Candidates"
          value={stats?.candidate}
          subtitle={`${stats?.active_candidate || 0} active`}
          icon="bi-people"
          color="text-purple-600"
          link="/candidate"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/create_job" className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              <i className="bi bi-plus-circle text-xl text-blue-600 mr-3"></i>
              <span className="font-medium text-gray-700">Post New Job</span>
            </Link>
            <Link to="/create_employer" className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <i className="bi bi-building-add text-xl text-green-600 mr-3"></i>
              <span className="font-medium text-gray-700">Add Employer</span>
            </Link>
            <Link to="/create_candidate" className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
              <i className="bi bi-person-plus text-xl text-purple-600 mr-3"></i>
              <span className="font-medium text-gray-700">Add Candidate</span>
            </Link>
            <Link to="/applyed_job" className="flex items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
              <i className="bi bi-file-earmark-text text-xl text-orange-600 mr-3"></i>
              <span className="font-medium text-gray-700">Applications</span>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">System Overview</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <i className="bi bi-check-circle text-green-500 mr-3"></i>
                <span className="text-gray-700">API Status</span>
              </div>
              <span className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">Active</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <i className="bi bi-database text-blue-500 mr-3"></i>
                <span className="text-gray-700">Database</span>
              </div>
              <span className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">Connected</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <i className="bi bi-phone text-purple-500 mr-3"></i>
                <span className="text-gray-700">Mobile App</span>
              </div>
              <span className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-full">Ready</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Manage Attributes</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { name: "States", link: "/state", icon: "bi-geo-alt" },
            { name: "Cities", link: "/city", icon: "bi-pin-map" },
            { name: "Job Positions", link: "/jobs_position", icon: "bi-diagram-3" },
            { name: "Skills", link: "/skills", icon: "bi-tools" },
            { name: "Education", link: "/education", icon: "bi-mortarboard" },
            { name: "Experience", link: "/experiance", icon: "bi-bar-chart" },
            { name: "Industry Types", link: "/industry_type", icon: "bi-buildings" },
            { name: "Employment Types", link: "/employment_type", icon: "bi-clock" },
            { name: "Salary Types", link: "/salary_type", icon: "bi-currency-dollar" },
            { name: "Workplaces", link: "/work_place", icon: "bi-house-door" },
            { name: "Promotions", link: "/promote", icon: "bi-megaphone" },
            { name: "Pages", link: "/pages", icon: "bi-file-text" },
          ].map((item) => (
            <Link
              key={item.name}
              to={item.link}
              className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center"
            >
              <i className={`bi ${item.icon} text-xl text-gray-600 mb-2`}></i>
              <span className="text-sm font-medium text-gray-700">{item.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Footer Branding */}
      <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 pt-8 pb-6 px-6 rounded-t-lg shadow-inner">
        <div className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4 mb-4 md:mb-0">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="bi bi-box text-purple-600 text-xl"></i>
            </div>
            <div>
              <p className="font-bold text-lg text-gray-800">
                Geekhub Tech Solutions
              </p>
              <p className="text-xs text-gray-500 mt-1">
                © 2026 Developed by Geekhub Tech Solutions
              </p>
            </div>
          </div>
          <div className="text-xs text-gray-400 font-medium">
            All rights reserved · v1.0
          </div>
        </div>
      </div>

    </div>
  );
};

export default LayoutHOC(Dashboard);
